async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) { try { data = JSON.parse(text); } catch { /* ignore */ } }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  get: <T,>(p: string) => request<T>('GET', p),
  post: <T,>(p: string, body?: unknown) => request<T>('POST', p, body),
  put: <T,>(p: string, body?: unknown) => request<T>('PUT', p, body),
};

export type MeResponse =
  | { kind: 'admin' }
  | { kind: 'user'; username: string }
  | { kind: 'anonymous' };

export interface AdminStats {
  userCount: number;
  recordingCount: number;
  diskBytes: number;
}

export interface AdminUser {
  id: number;
  username: string;
  created_at: number;
  recording_count: number;
  bytes_used: number;
}

export type Powertrain = 'engine' | 'ev';

export interface RecordingLabels {
  gauge_rpm: number | null;
  powertrain: Powertrain | null;
  incident_keywords: string | null;
}

/** Whether a recording's persisted analysis_json reflects the
 *  current pipeline schema (cycle_fingerprint included), an older
 *  schema (analyzed but pre-fingerprint), or has no analysis. */
export type AnalysisSchemaStatus = 'current' | 'stale' | 'none';

export interface AdminRecording {
  id: number;
  user_id: number;
  username: string;
  vehicle_id?: number | null;
  vehicle_name?: string | null;
  size_bytes: number;
  sample_rate: number;
  channels: number;
  duration_sec: number;
  created_at: number;
  analysis_status: string;
  analysis_schema?: AnalysisSchemaStatus;
  gauge_rpm?: number | null;
  powertrain?: Powertrain | null;
  incident_keywords?: string | null;
}

export interface AdminSettings {
  max_disk_mb: number;
  disk_bytes_used: number;
}

// ---- Drill-down types --------------------------------------------------

export type UnmixMethod = 'block' | 'ssa' | 'ica' | 'nmf';

export type RpmDetectorName = 'yin' | 'cepstrum' | 'hss' | 'stft';

export interface RpmMethodResult {
  method: RpmDetectorName;
  rpm: number | null;
  fDom_hz: number | null;
  error: string | null;
  lock_factor: number | null;
  corrected_rpm: number | null;
}

export type RpmAgreement = 'all' | 'majority' | 'split' | 'none';
export type RpmCorrectionKind = 'gauge' | 'ladder' | 'none';
export type MicLocation = 'phone_cab' | 'phone_hood' | 'external_hood';

export type AdminPowertrainKind = 'engine' | 'hybrid' | 'ev';

export interface RepairEvent {
  id: number;
  user_id: number;
  vehicle_id: number;
  cylinder_id: number | null;
  repair_kind: RepairKind;
  notes: string | null;
  occurred_at: number;
  recorded_at: number;
  recorded_by: 'user' | 'admin';
}

export const REPAIR_KINDS = [
  'spark_plug', 'ignition_coil', 'fuel_injector', 'compression_test',
  'valve_adjustment', 'oil_change', 'air_filter', 'fuel_filter',
  'pcv_valve', 'timing_chain', 'head_gasket', 'piston_ring',
  'exhaust_repair', 'intake_clean', 'belt_replace',
  'mass_airflow_clean', 'other',
] as const;
export type RepairKind = typeof REPAIR_KINDS[number];

export const REPAIR_KIND_LABELS: Record<RepairKind, string> = {
  spark_plug:         'Spark plug',
  ignition_coil:      'Ignition coil',
  fuel_injector:      'Fuel injector',
  compression_test:   'Compression test',
  valve_adjustment:   'Valve adjustment',
  oil_change:         'Oil + filter',
  air_filter:         'Air filter',
  fuel_filter:        'Fuel filter',
  pcv_valve:          'PCV valve',
  timing_chain:       'Timing chain',
  head_gasket:        'Head gasket',
  piston_ring:        'Piston ring',
  exhaust_repair:     'Exhaust repair',
  intake_clean:       'Intake / throttle clean',
  belt_replace:       'Accessory belt',
  mass_airflow_clean: 'MAF sensor clean',
  other:              'Other (see notes)',
};

/** Decode a vehicle's `persistent_cyl_assignments` JSON blob into a
 *  template_slot → physical_cyl_num map. Empty object on null/malformed. */
export function parseCylAssignments(json: string | null | undefined): Record<number, number> {
  if (!json) return {};
  try {
    const raw = JSON.parse(json) as Record<string, number>;
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const slot = Number(k);
      if (Number.isInteger(slot) && Number.isInteger(v)) out[slot] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export interface AdminVehicle {
  id: number;
  name: string;
  created_at: number;
  recording_count: number;
  bytes_used: number;
  num_cylinders?: number;
  num_strokes?: number;
  num_valves_per_cylinder?: number;
  firing_order?: string | null;
  powertrain_kind?: AdminPowertrainKind;
  /** JSON-encoded { "<template_slot>": physical_cyl_num } map for the
   *  cylinder calibration. Null until set via the calibration modal. */
  persistent_cyl_assignments?: string | null;
}

export interface AdminVehicleListResponse {
  user: { id: number; username: string; created_at: number };
  vehicles: AdminVehicle[];
}

export interface SimulationCheck {
  label: string;
  expected: string;
  got: string;
  ok: boolean;
}

export interface SimulationResult {
  recording_id: number;
  vehicle_id: number;
  user_id: number;
  requested: { rpm: number; duration_sec: number; seed: number };
  expected: {
    rpm: number;
    fDom_hz: number;
    cycle_samples: number;
    M: number;
    num_cylinders: number;
    num_strokes: number;
    firing_order: number[];
    cyl_funds: number[];
    cyl_n_harm: number[];
  };
  detected: {
    rpm: number | null;
    method_internal_rpm: number | null;
    fDom_hz: number | null;
    cycle_samples: number | null;
    cylinders_cycle_samples: number | null;
    cycles_analyzed: number | null;
    rpm_agreement: string | null;
    bootstrap: boolean | null;
    confidence: number | null;
    num_cyl_panels: number;
  };
  checks: SimulationCheck[];
  ok: boolean;
}

export interface AdminVehicleRecording {
  id: number;
  size_bytes: number;
  sample_rate: number;
  channels: number;
  duration_sec: number;
  created_at: number;
  analysis_status: string;
  analysis_schema?: AnalysisSchemaStatus;
  gauge_rpm?: number | null;
  powertrain?: Powertrain | null;
  incident_keywords?: string | null;
}

export interface AdminVehicleRecordingsResponse {
  vehicle: {
    id: number;
    name: string;
    user_id: number;
    username: string;
    num_cylinders?: number;
    num_strokes?: number;
    num_valves_per_cylinder?: number;
    firing_order?: string | null;
    powertrain_kind?: AdminPowertrainKind;
    persistent_cyl_assignments?: string | null;
  };
  recordings: AdminVehicleRecording[];
}

export interface AdminRecordingMetadata {
  recording_id: number;
  user_id: number;
  vehicle: { id: number; name: string };
  trip: { start_km: number; purpose: string };
  mic_location: MicLocation;
  engine?: {
    num_cylinders: number;
    num_strokes: number;
    num_valves_per_cylinder?: number;
    total_valves?: number;
  };
  recorded_at: number;
  sample_rate: number;
  channels: number;
  duration_sec: number;
  size_bytes: number;
  /** Merged in from the DB by the metadata endpoint. */
  labels?: RecordingLabels;
}

export interface RmsResult {
  block_size: number;
  sample_rate: number;
  num_blocks: number;
  sample_count: number;
  rms: number[];
}

export interface SpectrogramResult {
  block_size: number;
  sample_rate: number;
  num_blocks: number;
  num_bins: number;
  bin_hz_step: number;
  freq_min_hz: number;
  freq_max_hz: number;
  data: number[];
}

export interface ComponentsResult {
  sample_rate: number;
  method: UnmixMethod;
  num_components: number;
  display_length: number;
  singular_values: number[];
  components: number[][];

  // ICA-only fields
  rpm?: number;
  dominant_freq_hz?: number;
  num_cylinders?: number;
  num_strokes?: number;
  stroke_samples?: number;
  cycle_samples?: number;
  component_rms?: number[];
  error?: string;
  rpm_error?: string;
  rpm_methods?: RpmMethodResult[];
  rpm_agreement?: RpmAgreement;
  rpm_raw?: number;
  rpm_correction?: RpmCorrectionKind;
  rpm_diagnostic?: string;
  // NMF-only
  nmf_W?: number[][];
  nmf_H?: number[][];
  nmf_freqs_hz?: number[];
  nmf_phase_bins?: number;

  // Present only when admin requests includeFullWaveforms
  full_sample_rate?: number;
  full_length?: number;
  full_components?: number[][];
  ensemble_trials?: number[][];
  num_trials?: number;
  cylinders?: CylinderHealthResult;
  accessories?: AccessoryResult[];
  vacuum_leak?: VacuumLeakResult;
  bearings?: BearingResult;
  ac_cycling?: AcCyclingResult;
  events?: EventTimelineResult;
  powertrain_state?: PowertrainStateResult;
}

export interface BearingPeak {
  freq_hz: number;
  amplitude: number;
  snr: number;
  shaft_ratio: number;
  candidate: string;
}

export interface BearingResult {
  band_lo_hz?: number;
  band_hi_hz?: number;
  noise_floor?: number;
  shaft_rpm?: number;
  shaft_freq_hz?: number;
  peaks: BearingPeak[];
  error?: string;
}

export interface AcTransition {
  time_sec: number;
  kind: 'engage' | 'disengage';
}

export interface AcCyclingResult {
  expected_hz?: number;
  pct_running?: number;
  num_transitions?: number;
  transitions?: AcTransition[];
  health?: 'on' | 'off' | 'cycling' | 'short-cycling' | 'unknown';
  pattern?: string;
  error?: string;
}

export interface DetectedEvent {
  time_start_sec: number;
  time_end_sec: number;
  duration_sec: number;
  peak_rms: number;
  centroid_hz: number;
  bandwidth_hz: number;
  kind: 'click' | 'tonal-burst' | 'broadband-burst' | 'sustained';
}

export interface EventTimelineResult {
  background?: number;
  threshold?: number;
  events: DetectedEvent[];
  error?: string;
}

// ---- Trend samples ---------------------------------------------------

export interface TrendAccessory {
  name: string;
  snr: number | null;
  peak_hz: number | null;
  deviation_hz: number | null;
  health: string | null;
}

export interface TrendCylinderAlignment {
  rotation: number;
  confidence: number;
  bootstrap: boolean;
  template_count: number;
}

export interface TrendSample {
  recording_id: number;
  recorded_at: number;
  duration_sec: number;
  rpm: number | null;
  gauge_rpm: number | null;
  rpm_agreement: string | null;
  smoothness_score: number | null;
  exhaust_leak_score: number | null;
  vacuum_leak_score: number | null;
  global_health: string | null;
  num_cylinders: number | null;
  cylinder_energies: number[] | null;
  cylinder_variances: number[] | null;
  cylinder_missed: number[] | null;
  cylinder_decay: (number | null)[] | null;
  cylinder_knock: number[] | null;
  cylinder_phase: number[] | null;
  cylinder_alignment: TrendCylinderAlignment | null;
  cylinder_confidences: number[] | null;
  cylinder_confidences_by_cyl: number[] | null;
  cylinder_energies_by_cyl: number[] | null;
  cylinder_variances_by_cyl: number[] | null;
  cylinder_missed_by_cyl: number[] | null;
  cylinder_decay_by_cyl: (number | null)[] | null;
  cylinder_knock_by_cyl: number[] | null;
  cylinder_phase_by_cyl: number[] | null;
  accessories: TrendAccessory[] | null;
  bearing_peak_count: number | null;
  max_bearing_snr: number | null;
  ac_health: string | null;
  ac_pct_running: number | null;
  num_events: number | null;
  num_click_events: number | null;
  num_tonal_events: number | null;
  num_broadband_events: number | null;
  num_sustained_events: number | null;
}

export interface VehicleMetricBaseline {
  metric_key: string;
  n: number;
  mean: number;
  std: number;
  warn_low: number;
  warn_high: number;
}

export interface TrendsResponse {
  vehicle_id: number;
  samples: TrendSample[];
  metric_stats?: VehicleMetricBaseline[];
}

export interface AccessoryResult {
  name: string;
  pulley_ratio: number;
  expected_hz: number;
  peak_hz: number | null;
  peak_amplitude: number | null;
  median_in_band: number | null;
  snr: number | null;
  present: boolean;
  deviation_hz: number | null;
  health: 'green' | 'yellow' | 'absent' | 'unknown';
}

export interface VacuumLeakResult {
  band_lo_hz?: number;
  band_hi_hz?: number;
  total_energy?: number;
  comb_energy?: number;
  residual_energy?: number;
  score?: number;
  health?: 'green' | 'yellow' | 'red';
  error?: string;
}

export interface PerCylinderMetrics {
  index: number;
  slot_index: number;
  health: 'green' | 'yellow' | 'red';
  energy: number;
  energy_pct_of_cohort: number;
  energy_per_cycle: number[];
  variance_pct: number;
  missed_count: number;
  cycles_total: number;
  phase_offset_deg: number;
  high_freq_ratio: number;
  spectral_centroid_hz: number;
  spectrum_thumb: number[];
  knock_energy: number;
  knock_severity: number;
  decay_ratio: number | null;
  confidence: number;
  vs_cohort_t?: number;
  vs_cohort_p?: number;
  vs_cohort_d?: number;
  flags: string[];
  /** Steps N–R Layer-2 SVM output: predicted persistent template
   *  slot, P(predicted), and full per-class distribution. Absent
   *  until the per-vehicle classifier is trained. */
  classifier_template_slot?: number;
  classifier_probability?: number;
  classifier_probabilities?: number[];
}

export interface PowertrainStateResult {
  state: 'engine' | 'ev' | 'off' | 'unknown';
  confidence: number;
  harmonic_ratio: number;
  intensity_dbfs: number;
  reason?: string;
}

export interface CycleFingerprintAlignment {
  rotation: number;
  costs: number[];
  confidence: number;
  bootstrap: boolean;
  template_count: number;
  skipped?: string;
}

export interface CylinderHealthResult {
  num_cylinders: number;
  cycle_samples?: number;
  cycles_analyzed: number;
  anchor_offset?: number;
  firing_order?: number[];
  cohort_mean_energy?: number;
  global_health?: 'green' | 'yellow' | 'red';
  smoothness_score?: number;
  exhaust_leak_score?: number;
  cycle_fingerprint_alignment?: CycleFingerprintAlignment;
  cylinders: PerCylinderMetrics[];
  error?: string;
}

export interface OctaveDiagnostic {
  command: string[];
  stdout: string;
  stderr: string;
  duration_ms: number;
  exit_code: number;
}

export interface AnalysisDiagnostics {
  rms: OctaveDiagnostic;
  spectrogram: OctaveDiagnostic;
  components: OctaveDiagnostic;
}

export interface AnalysisResult {
  rms: RmsResult;
  spectrogram: SpectrogramResult;
  components: ComponentsResult;
  diagnostics?: AnalysisDiagnostics;
}

// ---- Steps N-R Layer-2 classifier (cylinder_classifier.m) -----------

/** Status payload from GET /api/admin/vehicles/:id/classifier. */
export interface ClassifierStatus {
  vehicle_id: number;
  trained: boolean;
  trained_at: number | null;
  n_train: number | null;
  accuracy: number | null;
  per_class_accuracy: Record<string, number> | null;
  confusion: number[][] | null;
  support_vectors: number | null;
  gamma: number | null;
  n_test: number | null;
  feature_dim: number | null;
  num_classes: number | null;
  reservoir_examples: number;
  current_slot_dim: number | null;
}

/** Result body from POST /api/admin/vehicles/:id/classifier/train. The
 *  same shape comes back on success (200) or refused-by-policy (422),
 *  with `ok` and `reason` distinguishing the two — UI should switch on
 *  `ok` rather than HTTP status. */
export interface ClassifierTrainResult {
  vehicle_id: number;
  ok: boolean;
  reason?: string;
  overall_accuracy?: number;
  per_class_accuracy?: Record<string, number>;
  confusion?: number[][];
  n_train?: number;
  n_test?: number;
  feature_dim?: number;
  num_classes?: number;
  support_vectors?: number;
  gamma?: number;
}
